<template>
  <div id="app">
    <!-- Mobile nav toggle -->
    <button
      v-if="isMobileLayout && route.meta.requiresAuth"
      type="button"
      class="mobile-nav-toggle"
      aria-label="打开菜单"
      @click="mobileNavOpen = true"
    >
      <span class="hamburger-line"></span>
      <span class="hamburger-line"></span>
      <span class="hamburger-line"></span>
    </button>

    <!-- Mobile backdrop -->
    <transition name="fade">
      <div
        v-if="isMobileLayout && mobileNavOpen && route.meta.requiresAuth"
        class="mobile-nav-backdrop"
        @click="mobileNavOpen = false"
      />
    </transition>

    <div class="app-container">
      <SideBar
        v-if="route.meta.requiresAuth"
        class="app-sidebar"
        :class="{ 'sidebar-open-mobile': mobileNavOpen }"
      />

      <main
        class="main-content"
        :class="{ 'has-sidebar': route.meta.requiresAuth }"
      >
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import SideBar from './components/SideBar.vue'

const route = useRoute()
const mobileNavOpen = ref(false)
const isMobileLayout = ref(false)

const MQ = 768

const updateMobileFlag = () => {
  if (typeof window === 'undefined') return
  isMobileLayout.value = window.innerWidth <= MQ
  if (!isMobileLayout.value) {
    mobileNavOpen.value = false
  }
}

onMounted(() => {
  updateMobileFlag()
  window.addEventListener('resize', updateMobileFlag)
})

onUnmounted(() => {
  window.removeEventListener('resize', updateMobileFlag)
})

watch(
  () => route.path,
  () => {
    mobileNavOpen.value = false
  }
)
</script>

<style>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  height: 100%;
  -webkit-text-size-adjust: 100%;
}

body {
  min-height: 100%;
  margin: 0;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
    Ubuntu, Cantarell, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  font: var(--app-font-body, inherit);
  background-color: var(--app-bg-page, #F8F9FE);
  color: var(--app-text-primary, #1A1D21);
  line-height: 1.6;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

a {
  text-decoration: none;
  color: inherit;
}

#app {
  min-height: 100%;
}

.app-container {
  display: flex;
  min-height: 100vh;
  min-height: 100dvh;
}

.main-content {
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-content.has-sidebar {
  margin-left: var(--app-sidebar-width, 260px);
}

/* ── Transition ── */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* ── Mobile Nav Toggle ── */
.mobile-nav-toggle {
  display: none;
}

.mobile-nav-backdrop {
  display: none;
}

@media (max-width: 768px) {
  .main-content.has-sidebar {
    margin-left: 0;
    padding-top: 0;
  }

  .mobile-nav-toggle {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    position: fixed;
    top: 16px;
    left: 16px;
    z-index: 120;
    width: 40px;
    height: 40px;
    padding: 0;
    border: none;
    border-radius: var(--app-radius-md, 10px);
    background: var(--app-bg-glass, rgba(255,255,255,0.72));
    box-shadow: var(--app-shadow-card, 0 2px 16px rgba(0,0,0,0.06));
    cursor: pointer;
    color: var(--app-text-primary);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  .hamburger-line {
    display: block;
    width: 18px;
    height: 2px;
    background: var(--app-text-primary);
    border-radius: 2px;
    transition: all 0.2s ease;
  }

  .mobile-nav-backdrop {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 98;
    background: var(--app-bg-overlay, rgba(45, 52, 54, 0.45));
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
  }

  .app-sidebar {
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .app-sidebar.sidebar-open-mobile {
    transform: translateX(0);
    box-shadow: 8px 0 40px rgba(0, 0, 0, 0.12);
  }
}
</style>
